import { forColumnHeaderWrapper } from "helpers/sf_utils"
import { dateFormatter } from "helpers/sf_analytics_helper"
import { FILTER_COMPONENT_TYPES } from "helpers/sf_constants"

export const BULK_EDIT_JOB_STATUSES = ["processing", "completed", "completed_with_errors", "failed", "cancelled"]

export const STATUS_DOT_CONFIG = {
    processing: { color: "sfyellow", label: "In Progress" },
    completed: { color: "sfgreen", label: "Completed" },
    completed_with_errors: { color: "sfyellow", label: "Completed With Errors" },
    failed: { color: "sfred", label: "Failed" },
    cancelled: { color: "text-muted", label: "Cancelled" },
}

export const capitalizeStatus = (status) => (status ? `${status.charAt(0).toUpperCase()}${status.slice(1)}` : "")

export const statusBadgeFormatter = (cell) => (
    <span className="d-flex align-items-center">
        <i className={`mdi mdi-circle font-size-14 me-2 ${STATUS_DOT_CONFIG[cell]?.color || "sfyellow"}`} />
        <span>{STATUS_DOT_CONFIG[cell]?.label || capitalizeStatus(cell) || "--"}</span>
    </span>
)

export const durationFormatter = (cell) => {
    if (cell === null || cell === undefined) {
        return "--"
    }
    const totalSeconds = Number(cell)
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60
    return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`
}

export const linkButtonFormatter = (label, onClick, className = "text-sfblue") => (
    <button
        type="button"
        className={`${className} bg-transparent border-0 p-0 text-start d-inline-block text-truncate`}
        style={{ maxWidth: "220px" }}
        title={label || ""}
        onClick={onClick}
    >
        {label || "--"}
    </button>
)

export const reasonFormatter = (cell, row, openMessageModal) => (
    cell
        ? linkButtonFormatter(cell, () => openMessageModal?.("Reason", cell, row))
        : "--"
)

export const errorMessageFormatter = (cell, row, openMessageModal) => (
    cell
        ? linkButtonFormatter(cell, () => openMessageModal?.("Error Message", cell, row), "text-danger")
        : "--"
)

export const triggeredByFormatter = (cell, row) => {
    const userId = row.admin_user_id || row.client_user_id
    const name = row.triggered_by_display_name || cell || "--"
    if (!userId) return name

    return `${name} (${userId})`
}

export const accountFormatter = (cell, row, onClickAccountLogin) => {
    const label = row.account_id || cell || "--"
    if (!row.account_id) return label

    return linkButtonFormatter(label, () => onClickAccountLogin?.(row.account_id))
}

export const companyNameFormatter = (cell) => cell || "--"

export const jobNameFormatter = (cell, row, openJobDetailsModal) => (
    linkButtonFormatter(cell, () => openJobDetailsModal?.(row))
)

const humanizeKey = (key) => key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())

export const changeFormatter = (cell, row) => {
    const changes = row.payload?.changes
    if (!changes || typeof changes !== "object") return "--"

    const summary = Object.entries(changes)
        .filter(([key, value]) => key !== "reason" && value !== null && value !== undefined && value !== "")
        .map(([key, value]) => `${humanizeKey(key)} → ${value}`)
        .join(", ")

    return summary || "--"
}

export const progressFormatter = (cell, row) => {
    const total = row.total_count ?? 0
    const processed = row.processed_count ?? 0
    const title = `Succeeded: ${row.succeeded_count ?? 0}, Failed: ${row.failed_count ?? 0}, Skipped: ${row.skipped_count ?? 0}`

    return <span title={title}>{`${processed} / ${total}`}</span>
}

export const BULK_EDIT_JOBS_FILTERS = {
    JOB_NAME: "job_name",
    STATUS: "status",
    ACCOUNT_ID: "account_id",
    TRIGGERED_BY: "triggered_by",
    TRIGGERED_AT: "triggered_at",
}

export const BULK_EDIT_JOB_STATUS_OPTIONS = BULK_EDIT_JOB_STATUSES.map((status) => ({
    value: status,
    label: capitalizeStatus(status),
}))

// Static definitions for the Filters modal (CustomProspectFilters). Dispatch is
// by component_type; see components/Common/CustomFilters/FilterTypes/index.js.
export const BULK_EDIT_JOBS_FILTER_OBJECTS = {
    [BULK_EDIT_JOBS_FILTERS.JOB_NAME]: {
        order: 0,
        comparator_value: "",
        value: "",
        label: "Job Name",
        name: BULK_EDIT_JOBS_FILTERS.JOB_NAME,
        component_type: FILTER_COMPONENT_TYPES.TEXT,
    },
    [BULK_EDIT_JOBS_FILTERS.STATUS]: {
        order: 1,
        comparator_value: "",
        value: "",
        label: "Status",
        name: BULK_EDIT_JOBS_FILTERS.STATUS,
        component_type: FILTER_COMPONENT_TYPES.SINGLE_SELECT,
        options: BULK_EDIT_JOB_STATUS_OPTIONS,
    },
    [BULK_EDIT_JOBS_FILTERS.ACCOUNT_ID]: {
        order: 2,
        comparator_value: "",
        value: "",
        label: "Account ID",
        name: BULK_EDIT_JOBS_FILTERS.ACCOUNT_ID,
        component_type: FILTER_COMPONENT_TYPES.TEXT,
    },
    [BULK_EDIT_JOBS_FILTERS.TRIGGERED_BY]: {
        order: 3,
        comparator_value: "",
        value: "",
        label: "Triggered By",
        name: BULK_EDIT_JOBS_FILTERS.TRIGGERED_BY,
        component_type: FILTER_COMPONENT_TYPES.TEXT,
    },
    [BULK_EDIT_JOBS_FILTERS.TRIGGERED_AT]: {
        order: 4,
        comparator_value: "",
        value: "",
        label: "Triggered At",
        name: BULK_EDIT_JOBS_FILTERS.TRIGGERED_AT,
        component_type: FILTER_COMPONENT_TYPES.DATE_RANGE,
    },
}

// Flattens the modal's active filter items into the simple {field: value} shape
// AdminBulkEditJobsHelper#apply_bulk_edit_jobs_filters expects on the backend.
export const mapBulkEditJobsFilters = (activeItems = []) => (
    activeItems.reduce((result, item) => {
        if (item.name === BULK_EDIT_JOBS_FILTERS.TRIGGERED_AT) {
            const range = item.value?.value
            if (range?.startDate) result.date_from = new Date(range.startDate).toISOString().slice(0, 10)
            if (range?.endDate) result.date_to = new Date(range.endDate).toISOString().slice(0, 10)
            return result
        }
        if (item.component_type === FILTER_COMPONENT_TYPES.SINGLE_SELECT || item.component_type === FILTER_COMPONENT_TYPES.MULTI_SELECT) {
            if (item.value?.value) result[item.name] = item.value.value
            return result
        }
        if (item.value) result[item.name] = item.value
        return result
    }, {})
)

export const bulkEditJobsColumns = ({ openJobDetailsModal, openMessageModal, onClickAccountLogin } = {}) => ([
    {
        dataField: "status",
        text: "Status",
        headerFormatter: forColumnHeaderWrapper,
        sort: true,
        formatter: statusBadgeFormatter,
    },
    {
        dataField: "job_name",
        text: "Job Name",
        headerFormatter: forColumnHeaderWrapper,
        sort: true,
        formatter: (cell, row) => jobNameFormatter(cell, row, openJobDetailsModal),
    },
    {
        dataField: "triggered_by_display_name",
        text: "Triggered By",
        headerFormatter: forColumnHeaderWrapper,
        sort: true,
        formatter: triggeredByFormatter,
    },
    {
        dataField: "account_id",
        text: "Account ID",
        headerFormatter: forColumnHeaderWrapper,
        sort: true,
        formatter: (cell, row) => accountFormatter(cell, row, onClickAccountLogin),
    },
    {
        dataField: "account_name",
        text: "Company Name",
        headerFormatter: forColumnHeaderWrapper,
        sort: true,
        formatter: companyNameFormatter,
    },
    {
        dataField: "payload",
        text: "Bulk Change to",
        headerFormatter: forColumnHeaderWrapper,
        sort: true,
        formatter: changeFormatter,
    },
    {
        dataField: "processed_count",
        text: "Progress",
        headerFormatter: forColumnHeaderWrapper,
        sort: true,
        formatter: progressFormatter,
    },
    {
        dataField: "reason",
        text: "Reason",
        headerFormatter: forColumnHeaderWrapper,
        sort: true,
        formatter: (cell, row) => reasonFormatter(cell, row, openMessageModal),
    },
    {
        dataField: "error_message",
        text: "Error Message",
        headerFormatter: forColumnHeaderWrapper,
        sort: true,
        formatter: (cell, row) => errorMessageFormatter(cell, row, openMessageModal),
    },
    {
        dataField: "duration_seconds",
        text: "Duration",
        headerFormatter: forColumnHeaderWrapper,
        sort: true,
        formatter: durationFormatter,
    },
    {
        dataField: "created_at",
        text: "Triggered At",
        headerFormatter: forColumnHeaderWrapper,
        sort: true,
        formatter: dateFormatter,
    },
    {
        dataField: "started_at",
        text: "Started At",
        headerFormatter: forColumnHeaderWrapper,
        sort: true,
        formatter: dateFormatter,
    },
    {
        dataField: "completed_at",
        text: "Completed At",
        headerFormatter: forColumnHeaderWrapper,
        sort: true,
        formatter: dateFormatter,
    },
])
